import type {
  DataProvider,
  GetListParams,
  GetOneParams,
  CreateParams,
  UpdateParams,
  UpdateManyParams,
  CreateManyParams,
  DeleteOneParams,
  DeleteManyParams,
  CustomParams,
  BaseRecord,
  GetListResponse,
  GetOneResponse,
  CreateResponse,
  UpdateResponse,
  UpdateManyResponse,
  CreateManyResponse,
  DeleteOneResponse,
  DeleteManyResponse,
  CustomResponse,
} from '@refinedev/core'
import type {
  TApiErrorResponse,
  TApiListResponse,
  TApiSingleResponse,
  TCustomError,
} from '@modules/types'
import { camelToSnake, convertApiResponse, convertCamelToSnake } from './utils'

export type TApiCoreConfig = {
  baseUrl: string
  customErrorHandler?: (response: Response) => Promise<never>
  customFilterHandler?: (
    filters: Array<{ field?: string; operator?: string; value?: unknown }>,
    url: URL,
  ) => void
}

const defaultHandleApiError = async (response: Response): Promise<never> => {
  let errorData: TApiErrorResponse

  try {
    errorData = await response.json()
  } catch {
    const error = new Error('HTTP error occurred') as TCustomError
    error.status = response.status
    error.statusCode = response.status
    throw error
  }

  if (errorData.error && typeof errorData.error === 'object' && errorData.error !== null) {
    const nestedError = errorData.error as Record<string, unknown>
    let errorMessage = 'HTTP error occurred'
    if (typeof nestedError.message === 'string') {
      errorMessage = nestedError.message
    } else if (typeof nestedError.name === 'string') {
      errorMessage = nestedError.name
    }

    if (nestedError.errors && typeof nestedError.errors === 'object') {
      const validationErrors = Object.entries(nestedError.errors)
        .map(([field, messages]) => {
          if (Array.isArray(messages)) {
            return `${field}: ${messages.join(', ')}`
          }
          return `${field}: ${messages}`
        })
        .join('; ')

      if (validationErrors) {
        errorMessage += `\nValidation errors: ${validationErrors}`
      }
    }

    const error = new Error(errorMessage) as TCustomError
    const statusCode = typeof nestedError.status === 'number' ? nestedError.status : response.status
    error.status = statusCode
    error.statusCode = statusCode
    error.data = nestedError
    throw error
  }

  // Handle simple error format
  const errorMessage = errorData.message || errorData.error || 'HTTP error occurred'
  const error = new Error(errorMessage) as TCustomError
  error.status = response.status
  error.statusCode = response.status
  error.data = errorData
  throw error
}

// `meta.rawPayloadKeys` names top-level keys whose contents must reach the backend with their own
// key names intact — a foreign format where the keys are the contract (a react-querybuilder query
// travels with `humanLabel`). Everything else still crosses the camelCase to snake_case boundary.
const readRawPayloadKeys = (meta: CustomParams['meta']): string[] => {
  const raw = meta?.rawPayloadKeys
  if (!Array.isArray(raw)) return []

  return raw.filter((key): key is string => typeof key === 'string')
}

const convertPayload = (payload: unknown, rawPayloadKeys: string[]): unknown => {
  const converted = convertCamelToSnake<Record<string, unknown>>(payload)

  if (rawPayloadKeys.length === 0 || typeof payload !== 'object' || payload === null) {
    return converted
  }

  const original = new Map(Object.entries(payload))

  rawPayloadKeys.forEach(key => {
    if (!original.has(key)) return

    converted[camelToSnake(key)] = original.get(key)
  })

  return converted
}

export type TTextResponse = {
  text: string
  complete: boolean
}

type TChunkRead = {
  done: boolean
  value?: Uint8Array
}

export type TReadableAnswer = {
  body: { getReader: () => { read: () => Promise<TChunkRead> } } | null
  text: () => Promise<string>
}

export const readsText = async (response: TReadableAnswer): Promise<TTextResponse> => {
  const reader = response.body?.getReader()
  if (!reader) {
    return { text: await response.text(), complete: true }
  }

  const decoder = new TextDecoder()

  const readsFrom = async (text: string): Promise<TTextResponse> => {
    let piece: TChunkRead
    try {
      piece = await reader.read()
    } catch {
      return { text, complete: false }
    }

    if (piece.done) return { text: text + decoder.decode(), complete: true }

    return readsFrom(text + decoder.decode(piece.value, { stream: true }))
  }

  return readsFrom('')
}

const isTextRequested = (meta: CustomParams['meta']): boolean => meta?.responseType === 'text'

const defaultFilterHandler = (
  filters: Array<{ field?: string; operator?: string; value?: unknown }>,
  url: URL,
) => {
  filters.forEach(filter => {
    if (filter.operator === 'contains' && filter.field === 'q') {
      url.searchParams.append('filter[q]', String(filter.value))
    } else if (filter.operator === 'eq') {
      url.searchParams.append(`filter[${filter.field}]`, String(filter.value))
    }
  })
}

export const createDataProvider = (config: TApiCoreConfig): DataProvider => {
  const { baseUrl, customErrorHandler, customFilterHandler } = config
  const handleApiError = customErrorHandler || defaultHandleApiError
  const filterHandler = customFilterHandler || defaultFilterHandler

  return {
    getList: async <TData extends BaseRecord = BaseRecord>({
      resource,
      pagination,
      filters,
      sorters,
      meta,
    }: GetListParams): Promise<GetListResponse<TData>> => {
      const url = new URL(`${baseUrl}/${resource}`)

      if (pagination) {
        url.searchParams.append('page', pagination.current?.toString() || '1')
        url.searchParams.append('per_page', pagination.pageSize?.toString() || '10')
      }

      if (filters) {
        filterHandler(filters, url)
      }

      if (sorters && sorters.length > 0) {
        const sorter = sorters[0]
        if (sorter) {
          const sortField = sorter.order === 'desc' ? `-${sorter.field}` : sorter.field
          url.searchParams.append('sort', sortField)
        }
      }

      if (meta?.expand) {
        url.searchParams.append('expand', meta.expand)
      }

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      })

      if (!response.ok) {
        await handleApiError(response)
      }

      const data = await response.json()
      const convertedData = convertApiResponse<{ items: TData[]; meta: { totalCount: number } }>(
        data,
      )

      const responseData = convertedData.items || []
      const responseTotal = convertedData.meta?.totalCount || responseData.length

      return {
        data: responseData,
        total: responseTotal,
      }
    },

    getOne: async <TData extends BaseRecord = BaseRecord>({
      resource,
      id,
      meta,
    }: GetOneParams): Promise<GetOneResponse<TData>> => {
      const url = new URL(`${baseUrl}/${resource}/${id}`)

      if (meta?.expand) {
        url.searchParams.append('expand', meta.expand)
      }

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      })

      if (!response.ok) {
        await handleApiError(response)
      }

      const data = await response.json()
      const convertedData = convertApiResponse<{ data: TData }>(data)
      const responseData = convertedData.data || convertedData

      return {
        data: responseData,
      }
    },

    create: async <TData extends BaseRecord = BaseRecord, TVariables = Record<string, unknown>>({
      resource,
      variables,
    }: CreateParams<TVariables>): Promise<CreateResponse<TData>> => {
      const response = await fetch(`${baseUrl}/${resource}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(convertCamelToSnake(variables)),
      })

      if (!response.ok) {
        await handleApiError(response)
      }

      const data = await response.json()
      const convertedData = convertApiResponse<{ data: TData }>(data)
      const responseData = convertedData.data || convertedData

      return {
        data: responseData,
      }
    },

    update: async <TData extends BaseRecord = BaseRecord, TVariables = Record<string, unknown>>({
      resource,
      id,
      variables,
      meta,
    }: UpdateParams<TVariables>): Promise<UpdateResponse<TData>> => {
      const method = meta?.method || 'PUT'
      const response = await fetch(`${baseUrl}/${resource}/${id}`, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(convertCamelToSnake(variables)),
      })

      if (!response.ok) {
        await handleApiError(response)
      }

      const data = await response.json()
      const convertedData = convertApiResponse<TApiSingleResponse<TData>>(data)

      return {
        data: convertedData.data,
      }
    },

    updateMany: async <
      TData extends BaseRecord = BaseRecord,
      TVariables = Record<string, unknown>,
    >({
      resource,
      ids,
      variables,
    }: UpdateManyParams<TVariables>): Promise<UpdateManyResponse<TData>> => {
      const response = await fetch(`${baseUrl}/${resource}/bulk-update`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(convertCamelToSnake({ ids, ...variables })),
      })

      if (!response.ok) {
        await handleApiError(response)
      }

      const data = await response.json()
      const convertedData = convertApiResponse<TApiListResponse<TData>>(data)

      return {
        data: convertedData.data,
      }
    },

    createMany: async <
      TData extends BaseRecord = BaseRecord,
      TVariables = Record<string, unknown>,
    >({
      resource,
      variables,
    }: CreateManyParams<TVariables>): Promise<CreateManyResponse<TData>> => {
      const response = await fetch(`${baseUrl}/${resource}/bulk-create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(convertCamelToSnake({ data: variables })),
      })

      if (!response.ok) {
        await handleApiError(response)
      }

      const data = await response.json()
      const convertedData = convertApiResponse<TApiListResponse<TData>>(data)

      return {
        data: convertedData.data,
      }
    },

    deleteOne: async <TData extends BaseRecord = BaseRecord, TVariables = Record<string, unknown>>({
      resource,
      id,
    }: DeleteOneParams<TVariables>): Promise<DeleteOneResponse<TData>> => {
      const response = await fetch(`${baseUrl}/${resource}/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      })

      if (!response.ok) {
        await handleApiError(response)
      }

      await response.json()

      return {
        data: { id } as TData,
      }
    },

    deleteMany: async <
      TData extends BaseRecord = BaseRecord,
      TVariables = Record<string, unknown>,
    >({
      resource,
      ids,
    }: DeleteManyParams<TVariables>): Promise<DeleteManyResponse<TData>> => {
      const response = await fetch(`${baseUrl}/${resource}/bulk-delete`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ ids }),
      })

      if (!response.ok) {
        await handleApiError(response)
      }

      await response.json()

      return {
        data: ids.map(id => ({ id })) as TData[],
      }
    },

    getApiUrl: () => baseUrl,

    custom: async <
      TData extends BaseRecord = BaseRecord,
      TQuery = Record<string, unknown>,
      TPayload = Record<string, unknown>,
    >({
      url,
      method,
      payload,
      headers,
      meta,
    }: CustomParams<TQuery, TPayload>): Promise<CustomResponse<TData>> => {
      const requestUrl = url.startsWith('http') ? url : `${baseUrl}${url}`

      const isFormData = payload instanceof FormData
      const isBlob = payload instanceof Blob

      let requestHeaders: Record<string, string> = {
        'Content-Type': 'application/json',
        ...headers,
      }
      if (isFormData) {
        requestHeaders = { ...headers }
      } else if (isBlob) {
        requestHeaders = { 'Content-Type': 'application/octet-stream', ...headers }
      }

      let requestBody: FormData | Blob | string | undefined
      if (payload) {
        if (isFormData || isBlob) {
          requestBody = payload
        } else {
          requestBody = JSON.stringify(convertPayload(payload, readRawPayloadKeys(meta)))
        }
      } else {
        requestBody = undefined
      }

      const response = await fetch(requestUrl, {
        method: method.toUpperCase(),
        headers: requestHeaders,
        credentials: 'include',
        body: requestBody,
      })

      if (!response.ok) {
        await handleApiError(response)
      }

      if (isTextRequested(meta)) {
        return { data: convertApiResponse<TData>(await readsText(response)) }
      }

      const data = await response.json()
      const convertedData = convertApiResponse<TApiSingleResponse<TData>>(data)

      const responseData = convertedData.data || convertedData

      return {
        data: responseData,
      }
    },
  }
}
